import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Users, FileText, Receipt, Search, TrendingUp, Bell } from "lucide-react";
import { saas } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, kr } from "@/components/Layout";

const Stat = ({ icon: Icon, label, value, to, testid }) => (
  <Link to={to} data-testid={testid} className="rounded-2xl border border-border bg-card p-5 hover:border-primary/50 hover:-translate-y-0.5 transition-[transform,border-color]">
    <Icon className="h-5 w-5 text-primary" />
    <div className="mt-3 font-heading text-2xl font-extrabold">{value}</div>
    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
  </Link>
);

const JobRow = ({ j }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
    <div className="w-24 text-xs font-bold text-primary">{j.date.slice(5)} {j.start_time}</div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold truncate">{j.title}</div>
      <div className="text-xs text-muted-foreground truncate">{j.address}</div>
    </div>
    <span className="text-xs rounded-full bg-secondary px-2 py-0.5">{j.status}</span>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { saas.dashboard().then(setD); }, []);
  if (!d) return <div className="p-8 text-muted-foreground">Laster...</div>;

  return (
    <div className="p-4 sm:p-8 max-w-6xl" data-testid="dashboard-page">
      <PageHeader title={`Hei, ${user?.name?.split(" ")[0] || ""}`} subtitle={user?.company_name} />
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat icon={CalendarDays} label="Oppdrag i dag" value={d.today_jobs.length} to="/pro/kalender" testid="stat-today-jobs" />
        <Stat icon={Users} label="Kunder" value={d.customer_count} to="/pro/kunder" testid="stat-customers" />
        <Stat icon={Search} label="Nye prospekter" value={d.new_prospects} to="/pro/prospekter" testid="stat-prospects" />
        <Stat icon={FileText} label="Tilbud ute" value={d.pending_quotes} to="/pro/tilbud" testid="stat-quotes" />
        <Stat icon={Receipt} label={`Ubetalt (${d.unpaid_count})`} value={kr(d.unpaid_amount)} to="/pro/fakturaer" testid="stat-unpaid" />
        <Stat icon={TrendingUp} label="Inntekt denne mnd" value={kr(d.revenue_month)} to="/pro/fakturaer" testid="stat-revenue" />
      </div>
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="today-jobs-list">
          <h2 className="font-heading font-bold mb-2">Dagens oppdrag</h2>
          {d.today_jobs.length ? d.today_jobs.map((j) => <JobRow key={j.id} j={j} />) : <p className="text-sm text-muted-foreground">Ingen oppdrag i dag.</p>}
          <h2 className="font-heading font-bold mt-5 mb-2">Neste 7 dager</h2>
          {d.upcoming_jobs.length ? d.upcoming_jobs.slice(0, 8).map((j) => <JobRow key={j.id} j={j} />) : <p className="text-sm text-muted-foreground">Ingen kommende oppdrag.</p>}
        </section>
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="followups-list">
          <h2 className="font-heading font-bold mb-2 flex items-center gap-2"><Bell className="h-4 w-4" /> Oppfølging i dag</h2>
          {d.followups.length ? d.followups.map((c) => (
            <Link key={c.id} to="/pro/kunder" className="block py-2 border-b border-border last:border-0 text-sm">
              <span className="font-semibold">{c.name}</span> <span className="text-muted-foreground">· {c.next_followup}</span>
            </Link>
          )) : <p className="text-sm text-muted-foreground">Ingen oppfølginger forfaller.</p>}
        </section>
      </div>
    </div>
  );
}
